import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';

const EVENT_LABELS = {
  'login_success': { label: 'Login Success', color: 'bg-pista/20 text-pista' },
  'login_success_google': { label: 'Google Login', color: 'bg-pista/20 text-pista' },
  'login_success_discord': { label: 'Discord Login', color: 'bg-pista/20 text-pista' },
  'login_failed': { label: 'Login Failed', color: 'bg-destructive/20 text-destructive' },
  'user_registered': { label: 'Registered', color: 'bg-blue-500/20 text-blue-400' },
  'user_registered_google': { label: 'Registered (Google)', color: 'bg-blue-500/20 text-blue-400' },
  'user_registered_discord': { label: 'Registered (Discord)', color: 'bg-blue-500/20 text-blue-400' },
  'password_reset_requested': { label: 'Reset Requested', color: 'bg-orange-500/20 text-orange-400' },
  'password_reset_completed': { label: 'Password Reset', color: 'bg-orange-500/20 text-orange-400' },
};

const AdminAuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (eventTypeFilter && eventTypeFilter !== 'all') {
        params.append('event_type', eventTypeFilter);
      }
      const res = await apiClient.get(`/admin/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [eventTypeFilter]);

  return (
    <div className="space-y-4" data-testid="admin-audit-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {logs.length} of {total} events
        </p>
        <div className="w-64">
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger data-testid="event-type-filter">
              <SelectValue placeholder="Filter by event type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="login_success">Login Success</SelectItem>
              <SelectItem value="login_failed">Login Failed</SelectItem>
              <SelectItem value="user_registered">User Registered</SelectItem>
              <SelectItem value="password_reset_requested">Password Reset Requests</SelectItem>
              <SelectItem value="password_reset_completed">Password Resets</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-10 h-10 border-4 border-pista border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No audit logs found
            </div>
          ) : (
            <div className="divide-y divide-border">
              <div className="px-4 py-3 flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <div className="w-52">Time</div>
                <div className="w-56">Event</div>
                <div className="flex-1">User</div>
                <div className="w-52">Details</div>
              </div>
              {logs.map((log) => {
                const eventConfig = EVENT_LABELS[log.event_type] || { label: log.event_type, color: 'bg-secondary text-muted-foreground' };
                return (
                  <div
                    key={log.event_id}
                    className="px-4 py-3 flex items-center text-sm hover:bg-secondary/40 transition-colors"
                    data-testid={`audit-log-${log.event_id}`}
                  >
                    <div className="w-52 text-xs font-mono text-muted-foreground">
                      {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                    </div>
                    <div className="w-56">
                      <Badge className={`${eventConfig.color} text-xs border-0`} variant="outline">
                        {eventConfig.label}
                      </Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium text-xs">{log.user?.name || 'Unknown'}</p>
                      <p className="truncate text-xs text-muted-foreground">{log.user?.email || log.user_id}</p>
                    </div>
                    <div className="w-52 text-xs text-muted-foreground truncate">
                      {log.metadata && Object.keys(log.metadata).length > 0
                        ? Object.entries(log.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')
                        : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditPage;
